// Microsoft Entra ID sign-in via MSAL. This module's only job is to produce an ID token;
// AuthContext then exchanges it at /api/portal/login/sso for the normal Orbit session
// token, so nothing outside the login flow knows SSO exists.
//
// Configured at build time; when either var is missing the login page simply does not
// offer the button, which is how local/dev environments opt out.
import { PublicClientApplication, BrowserUtils } from '@azure/msal-browser';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';

const CLIENT_ID = import.meta.env.VITE_AAD_CLIENT_ID;
const TENANT_ID = import.meta.env.VITE_AAD_TENANT_ID;

export const ssoEnabled = Boolean(CLIENT_ID && TENANT_ID);

// Lazy singleton: MSAL v3 requires an async initialize() before any call, and outside a
// sign-in (or a return from one) there is no reason to pay for it.
let _instance = null;
async function getInstance() {
  if (!_instance) {
    const app = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin,
        // Resolve the redirect return at the redirectUri itself instead of bouncing back
        // to the page that started it. The bounce is a second navigation that strips the
        // response hash before the module-load gate below can see it; landing at '/'
        // signed-in is the better trade.
        navigateToLoginRequestUrl: false,
      },
      // Session storage over local storage: the MSAL cache is only needed for the sign-in
      // round-trip — Orbit's own token is the session — so nothing should outlive the tab.
      cache: { cacheLocation: 'sessionStorage' },
    });
    await app.initialize();
    _instance = app;
  }
  return _instance;
}

const LOGIN_REQUEST = {
  scopes: ['openid', 'profile', 'email'],
  // Always show the account picker: on a shared/roaming machine the silently-remembered
  // account is exactly the wrong default, and signing in is rare enough that one extra
  // click costs nothing.
  prompt: 'select_account',
};

// Popup first; when the environment refuses to open one (corporate popup blockers,
// embedded browsers), fall back to the full-page redirect flow — that call navigates
// away, so it never resolves. The result then comes back through `redirectResult`
// below on the next page load.
async function tryLogin(msal) {
  try {
    const result = await msal.loginPopup(LOGIN_REQUEST);
    return result.idToken;
  } catch (err) {
    if (err?.errorCode === 'popup_window_error' || err?.errorCode === 'empty_window_error') {
      await msal.loginRedirect(LOGIN_REQUEST);
      return new Promise(() => {}); // navigating away — nothing further runs
    }
    throw err;
  }
}

/** Run the Microsoft sign-in and return the ID token to exchange. */
export async function acquireIdToken() {
  const msal = await getInstance();
  try {
    return await tryLogin(msal);
  } catch (err) {
    // A sign-in abandoned partway (the tab left Microsoft's page without completing)
    // leaves MSAL's session cache claiming an interaction is still running, which then
    // fails every later attempt. A click on the login button cannot coincide with a
    // genuine one, so the claim is stale: clear it and retry once.
    if (err?.errorCode === 'interaction_in_progress') {
      try {
        Object.keys(sessionStorage).filter(k => k.includes('msal'))
          .forEach(k => sessionStorage.removeItem(k));
      } catch {}
      return tryLogin(msal);
    }
    throw err;
  }
}

// The return leg of both SSO flows, kicked off AT MODULE LOAD — before React mounts and
// before the hand-rolled router can replaceState the auth response out of the URL. Idle
// page loads skip it entirely (no response hash, no MSAL init), which keeps ordinary
// startup free of MSAL work; resolves to an ID token only on a genuine return from
// Microsoft, and null in every other case including MSAL errors (surfaced to the user as
// simply not being signed in, which is what they are).
//
// The popup branch exists because of a breaking change in MSAL v5: the opener no longer
// polls the popup's URL for the auth response — its loginPopup waits on a BroadcastChannel
// that the page loaded at the redirectUri must post to. Our redirectUri is the SPA itself,
// so when this load IS the popup returning from Microsoft (MSAL's state parameter says
// interactionType=popup), this module plays that bridge role: broadcast the response to
// the opener — which then redeems the code and resolves loginPopup — and let MSAL close
// this window. Without it the popup lands back on the app, the router strips the code from
// the URL, and the opener waits in silence until it times out.
export const redirectResult = (async () => {
  if (!ssoEnabled) return null;
  const h = window.location.hash;
  if (!h.includes('code=') && !h.includes('error=')) return null;
  try {
    if (BrowserUtils.isInPopup()) {
      await broadcastResponseToMainFrame(); // closes this window
      return null;
    }
    // Redirect-flow return (the blocked-popup fallback): same tab, no opener to relay
    // to — hand MSAL the response directly.
    const msal = await getInstance();
    const result = await msal.handleRedirectPromise();
    return result?.idToken || null;
  } catch {
    return null;
  }
})();
